import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import notificationService from '../../services/notificationService';
import automatedVerificationService from '../../services/automatedVerificationService';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeUrl } from '../../security/sanitize';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useFeedback } from '../../contexts/FeedbackContext';
import Breadcrumb from '../../components/common/Breadcrumb';

// Icons
import {
  Image,
  Link,
  X,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const ProofSubmission = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, showError } = useFeedback();
  
  const [existingProof, setExistingProof] = useState(null);
  const [isResubmission, setIsResubmission] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  
  const [formData, setFormData] = useState({
    postUrl: '',
    additionalNotes: ''
  });
  
  const [screenshots, setScreenshots] = useState([]);
  const [screenshotUrls, setScreenshotUrls] = useState([]);
  const [screenshotUploading, setScreenshotUploading] = useState(false);
  
  const [contentFile, setContentFile] = useState(null);
  const [contentUrl, setContentUrl] = useState('');
  const [contentUploading, setContentUploading] = useState(false);
  
  const [verification, setVerification] = useState({
    checking: false,
    results: null
  });
  
  const [trackingCode, setTrackingCode] = useState(null);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!currentUser || !campaignId) {
        setLoading(false);
        return;
      }

      try {
        // Get campaign document
        const campaignRef = doc(db, 'campaigns', campaignId);
        const campaignSnap = await getDoc(campaignRef);
        
        if (!campaignSnap.exists()) {
          console.error('Campaign not found');
          setLoading(false);
          return;
        }
        
        const campaignData = campaignSnap.data();
        
        // Get brand information
        const brandRef = doc(db, 'users', campaignData.brandId);
        const brandSnap = await getDoc(brandRef);
        let brandData = {};
        
        if (brandSnap.exists()) {
          brandData = brandSnap.data();
        }
        
        // Check if user is a participant
        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', campaignId),
          where('influencerId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          showError('You are not an approved participant for this campaign.', 'You need to apply and be approved before submitting proof.', 6000);
          navigate(`/influencer/campaigns/${campaignId}`);
          return;
        }
        
        const participantData = querySnapshot.docs[0].data();
        const participantId = querySnapshot.docs[0].id;
        
        if (participantData.status !== 'approved') {
          showError(`Your application status is "${participantData.status}".`, 'Only approved participants can submit proof.', 6000);
          navigate(`/influencer/campaigns/${campaignId}`);
          return;
        }

        // Check draft approval status for pre-approval workflow
        if (!participantData.draftStatus || participantData.draftStatus !== 'approved') {
          let errorMessage = 'You need to submit and get your content draft approved before submitting proof.';
          let detailMessage = 'Please submit your content draft for brand approval first.';
          
          if (participantData.draftStatus === 'pending') {
            errorMessage = 'Your content draft is still pending brand approval.';
            detailMessage = 'Please wait for the brand to approve your draft before submitting proof.';
          } else if (participantData.draftStatus === 'revision_requested') {
            errorMessage = 'Your content draft needs revision.';
            detailMessage = 'Please check the feedback and resubmit your draft before submitting proof.';
          }
          
          showError(errorMessage, detailMessage, 6000);
          navigate(`/influencer/campaigns/${campaignId}`);
          return;
        }

        // Get tracking link for this participant to use in engagement verification
        let trackingCode = null;
        if (participantData.trackingCode) {
          trackingCode = participantData.trackingCode;
        } else {
          // Try to get tracking link from trackingLinks collection
          try {
            const trackingLinksRef = collection(db, 'trackingLinks');
            const trackingQuery = query(
              trackingLinksRef,
              where('campaignId', '==', campaignId),
              where('influencerId', '==', currentUser.uid),
              where('isActive', '==', true)
            );
            
            const trackingSnapshot = await getDocs(trackingQuery);
            if (!trackingSnapshot.empty) {
              const trackingData = trackingSnapshot.docs[0].data();
              trackingCode = trackingData.trackingCode;
            }
          } catch (trackingError) {
            console.error('Error fetching tracking code:', trackingError);
          }
        }
        
        // Store tracking code for use in verification
        setTrackingCode(trackingCode);
        
        // Check for existing proof submission
        if (participantData.proofId) {
          try {
            const proofRef = doc(db, 'proofs', participantData.proofId);
            const proofSnap = await getDoc(proofRef);
            
            if (proofSnap.exists()) {
              const proofData = proofSnap.data();
              setExistingProof({ id: proofSnap.id, ...proofData });
              
              // Check if this is a resubmission (revision requested or rejected)
              if (proofData.status === 'rejected' || participantData.submissionStatus === 'revision_requested') {
                setIsResubmission(true);
                setRevisionFeedback(participantData.revisionFeedback || proofData.brandFeedback || '');
                
                // Pre-fill form with existing data
                setFormData({
                  postUrl: proofData.postUrl || '',
                  additionalNotes: proofData.additionalNotes || ''
                });
                
                // Set existing screenshot URLs
                if (proofData.screenshotUrls && proofData.screenshotUrls.length > 0) {
                  setScreenshotUrls(proofData.screenshotUrls);
                }
                
                // Set existing content URL
                if (proofData.contentUrl) {
                  setContentUrl(proofData.contentUrl);
                }
              } else if (proofData.status === 'pending' || proofData.status === 'approved') {
                // Already submitted and pending/approved - show read-only view
                setExistingProof({ id: proofSnap.id, ...proofData });
                setFormData({
                  postUrl: proofData.postUrl || '',
                  additionalNotes: proofData.additionalNotes || ''
                });
                
                // Set existing screenshot URLs and content URL for display
                if (proofData.screenshotUrls && proofData.screenshotUrls.length > 0) {
                  setScreenshotUrls(proofData.screenshotUrls);
                }
                if (proofData.contentUrl) {
                  setContentUrl(proofData.contentUrl);
                }
                
                // Show success message for recently submitted proof
                showSuccess(
                  proofData.status === 'approved' ? 'Proof Approved!' : 'Proof Submitted Successfully!',
                  proofData.status === 'approved' 
                    ? 'Your proof has been approved by the brand.' 
                    : 'Your proof is currently under review by the brand.',
                  4000
                );
              }
            }
          } catch (proofError) {
            console.error('Error fetching existing proof:', proofError);
          }
        }
        
        setCampaign({
          id: campaignSnap.id,
          title: campaignData.name || 'Unnamed Campaign',
          brand: {
            id: campaignData.brandId,
            name: brandData.displayName || brandData.companyName || 'Unknown Brand',
            logo: brandData.logo || brandData.profileImageUrl || '/assets/default-logo.png',
            handle: brandData.socialMedia?.instagram || brandData.brandHandle || ''
          },
          requirements: campaignData.contentGuidelines || 'No specific requirements provided',
          deadline: campaignData.endDate || 'No deadline specified',
          requiredHashtags: campaignData.requiredHashtags || [],
          requiredMentions: campaignData.requiredMentions || '',
          ...campaignData // Include all other campaign data fields
        });
      } catch (error) {
        console.error("Error fetching campaign details:", error);
        showError('Failed to load campaign details.', 'Please check your connection and try again.', 6000);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignDetails();
  }, [currentUser, campaignId, navigate, showError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScreenshotChange = (e) => {
    const files = Array.from(e.target.files);
    setScreenshots(prev => [...prev, ...files]);
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const handleContentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setContentFile(file);
    }
  };

  const uploadScreenshots = async () => {
    if (screenshots.length === 0) return [];
    
    setScreenshotUploading(true);
    const urls = [];
    
    try {
      for (const screenshot of screenshots) {
        const screenshotRef = ref(storage, `proofs/${campaignId}/${currentUser.uid}/screenshots/${Date.now()}_${screenshot.name}`);
        await uploadBytes(screenshotRef, screenshot);
        const url = await getDownloadURL(screenshotRef);
        urls.push(url);
      }
      
      setScreenshotUrls(urls);
      return urls;
    } catch (error) {
      console.error('Error uploading screenshots:', error);
      throw error;
    } finally {
      setScreenshotUploading(false);
    }
  };

  const uploadContentFile = async () => {
    if (!contentFile) return '';
    
    setContentUploading(true);
    
    try {
      const contentRef = ref(storage, `proofs/${campaignId}/${currentUser.uid}/content/${Date.now()}_${contentFile.name}`);
      await uploadBytes(contentRef, contentFile);
      const url = await getDownloadURL(contentRef);
      
      setContentUrl(url);
      return url;
    } catch (error) {
      console.error('Error uploading content file:', error);
      throw error;
    } finally {
      setContentUploading(false);
    }
  };

  const runAutomatedVerification = async (postUrl) => {
    setVerification({ checking: true, results: null });
    
    try {
      // Prepare hashtags - convert string to array if needed
      let hashtags = campaign?.requiredHashtags || [];
      if (typeof hashtags === 'string') {
        // Split by space, comma, or newline and filter out empty strings
        hashtags = hashtags.split(/[\s,\n]+/).filter(tag => tag.trim() !== '');
      }
      
      // Prepare required mentions - convert string to array if needed
      let requiredMentions = campaign?.requiredMentions || [];
      if (typeof requiredMentions === 'string') {
        // Split by space, comma, or newline and filter out empty strings
        requiredMentions = requiredMentions.split(/[\s,\n]+/).filter(mention => mention.trim() !== '');
      }
      
      // Use the first required mention as brandHandle, or fall back to brand profile handle
      const brandHandle = requiredMentions.length > 0 ? requiredMentions[0] : (campaign?.brand?.handle || '');
      
      // Call real automated verification service
      const verificationParams = {
        hashtags: hashtags,
        requiredMentions: requiredMentions,
        brandHandle: brandHandle
      };
      
      const results = await automatedVerificationService.verifyContent(
        postUrl,
        campaignId,
        currentUser.uid,
        verificationParams,
        trackingCode
      );
      
      setVerification({ checking: false, results });
      return results;
    } catch (error) {
      console.error('Error running verification:', error);
      showError('Failed to run automated verification.', 'Please check your content and try again.', 5000);
      setVerification({ checking: false, results: null });
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) return;
    
    // Get CSRF token
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Validate form
    const sanitizedPostUrl = sanitizeUrl(formData.postUrl);
    
    if (!sanitizedPostUrl || sanitizedPostUrl.trim() === '') {
      showError("Please provide the post URL", 'A link to your published content is required.', 5000);
      return;
    }
    
    // For resubmission, allow keeping existing files
    if (!isResubmission && screenshots.length === 0) {
      showError("Screenshot required.", "Please upload at least one screenshot of your published content.", 5000);
      return;
    }
    
    if (!isResubmission && !contentFile) {
      showError("Content file required.", "Please upload a video or photo of your content.", 5000);
      return;
    }
    
    // For resubmission, check if we have either new files or existing URLs
    if (isResubmission) {
      if (screenshots.length === 0 && screenshotUrls.length === 0) {
        showError("Please upload at least one screenshot", 'Screenshots of your published content are required for verification.', 5000);
        return;
      }
      
      if (!contentFile && !contentUrl) {
        showError("Please upload a video or photo of your content", 'A video or photo file of your content is required.', 5000);
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      // Run automated verification
      const verificationResults = await runAutomatedVerification(sanitizedPostUrl);
      
      // Check required verifications
      const requiredChecksFailed = verificationResults && 
        Object.values(verificationResults.required).some(check => !check.passed);
      
      if (requiredChecksFailed) {
        showError("Required verification checks failed.", 'Please fix the issues and try again.', 6000);
        setSubmitting(false);
        return;
      }
      
      // Upload new files if provided, otherwise keep existing URLs
      let finalScreenshotUrls = screenshotUrls; // Keep existing if any
      let finalContentUrl = contentUrl; // Keep existing if any
      
      if (screenshots.length > 0) {
        finalScreenshotUrls = await uploadScreenshots();
      }
      
      if (contentFile) {
        finalContentUrl = await uploadContentFile();
      }
      
      let proofRef;
      
      if (isResubmission && existingProof) {
        // Update existing proof document
        const existingProofRef = doc(db, 'proofs', existingProof.id);
        await updateDoc(existingProofRef, {
          postUrl: sanitizedPostUrl,
          additionalNotes: sanitizeText(formData.additionalNotes),
          screenshotUrls: finalScreenshotUrls,
          contentUrl: finalContentUrl,
          contentType: contentFile ? (contentFile.type.startsWith('video') ? 'video' : 'photo') : existingProof.contentType,
          verificationResults,
          status: 'pending',
          resubmittedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        proofRef = { id: existingProof.id };
      } else {
        // Create new proof submission document
        proofRef = await addDoc(collection(db, 'proofs'), {
          campaignId,
          influencerId: currentUser.uid,
          brandId: campaign.brand.id,
          postUrl: sanitizedPostUrl,
          additionalNotes: sanitizeText(formData.additionalNotes),
          screenshotUrls: finalScreenshotUrls,
          contentUrl: finalContentUrl,
          contentType: contentFile.type.startsWith('video') ? 'video' : 'photo',
          verificationResults,
          status: 'pending',
          campaignTitle: sanitizeText(campaign.title),
          influencerName: sanitizeText(currentUser.displayName) || 'Influencer',
          influencerEmail: currentUser.email || '',
          influencerProfileImage: currentUser.photoURL || '',
          submittedAt: serverTimestamp()
        });
      }
      
      // Update campaign participant status
      const participantsRef = collection(db, 'campaignParticipants');
      const q = query(
        participantsRef,
        where('campaignId', '==', campaignId),
        where('influencerId', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const participantId = querySnapshot.docs[0].id;
        const participantRef = doc(db, 'campaignParticipants', participantId);
        
        const updateData = {
          submissionStatus: 'submitted',
          proofId: proofRef.id
        };
        
        // For resubmission, clear revision fields
        if (isResubmission) {
          updateData.revisionFeedback = '';
          updateData.revisionRequestedAt = null;
          updateData.resubmissionDate = serverTimestamp();
        } else {
          updateData.submissionDate = serverTimestamp();
        }
        
        await updateDoc(participantRef, updateData);
      }
      
      // Send notification to brand
      try {
        await notificationService.createNotification(
          campaign.brand.id,
          'proof_submission',
          isResubmission ? 'Proof Resubmitted' : 'New Proof Submission',
          `${currentUser.displayName || 'Influencer'} has ${isResubmission ? 'resubmitted' : 'submitted'} proof for campaign: ${campaign.title}`,
          {
            relatedId: proofRef.id,
            relatedType: 'proof',
            campaignId,
            influencerId: currentUser.uid
          }
        );
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }
      
      showSuccess("Proof submitted successfully!", "Your proof has been submitted and is now pending brand review.", 4000);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/influencer/campaigns');
      }, 2000);
    } catch (error) {
      console.error("Error submitting proof:", error);
      showError("Failed to submit proof.", 'Please check your connection and try again.', 6000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="pb-12 relative">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign Not Found</h3>
          <p className="text-gray-600 mb-4">The campaign you're looking for doesn't exist or has been removed.</p>
          <Breadcrumb
            items={[
              { label: 'Campaigns', to: '/influencer/campaigns' },
              { label: 'Proof Submission' },
            ]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12 relative">
      <Breadcrumb
        items={[
          { label: 'Campaigns', to: '/influencer/campaigns' },
          { label: 'Campaign', to: `/influencer/campaigns/${campaignId}` },
          { label: 'Proof Submission' },
        ]}
      />
      
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center">
            {campaign.brand?.logo && campaign.brand.logo !== '/assets/default-logo.png' ? (
              <img 
                src={campaign.brand.logo} 
                alt={campaign.brand?.name || 'Brand'} 
                className="h-10 w-10 rounded-full mr-3"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/default-logo.png';
                }}
              />
            ) : (
              <img 
                src="/assets/default-logo.png" 
                alt={campaign.brand?.name || 'Brand'} 
                className="h-10 w-10 rounded-full mr-3"
              />
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {isResubmission ? 'Resubmit Proof' : 'Submit Proof'} for {campaign.title}
              </h3>
              <p className="text-sm text-gray-500">
                {isResubmission ? 'Update and resubmit your proof based on brand feedback' : 'Upload proof of your published content'}
              </p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <CSRFToken />
          
          {/* Revision Request Alert */}
          {isResubmission && revisionFeedback && (
            <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-orange-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-orange-800">
                    Revision Requested
                  </h3>
                  <div className="mt-2 text-sm text-orange-700">
                    <p className="mb-2">The brand has requested revisions to your previous submission. Please review the feedback below and make the necessary changes.</p>
                    <div className="mt-3 p-3 bg-white rounded border border-orange-200">
                      <p className="text-sm font-medium text-gray-900 mb-1">Brand Feedback:</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{revisionFeedback}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Campaign Requirements Reminder */}
          <div className="mb-6 p-4 bg-blue-50 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Campaign Requirements</h4>
            <div className="text-sm text-blue-700 whitespace-pre-wrap font-mono leading-relaxed">
              {campaign.requirements}
            </div>
            <p className="text-sm text-blue-700 mt-3 pt-3 border-t border-blue-200">
              <span className="font-medium">Deadline:</span> {campaign.deadline}
            </p>
          </div>
          
          {/* Post URL */}
          <div className="mb-6">
            <label htmlFor="postUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Post URL <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center">
              <Link className="h-5 w-5 text-gray-400 mr-2" />
              <input
                type="url"
                id="postUrl"
                name="postUrl"
                value={formData.postUrl}
                onChange={handleInputChange}
                onBlur={() => formData.postUrl && runAutomatedVerification(formData.postUrl)}
                className="flex-1 block border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="https://instagram.com/p/..."
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Paste the full URL of your Instagram/TikTok/Facebook post
            </p>
          </div>
          
          {/* Automated Verification Results */}
          {verification.checking && (
            <div className="mb-6 p-4 bg-gray-50 rounded-md">
              <div className="flex items-center">
                <LoadingSpinner size="sm" color="primary" />
                <span className="ml-2 text-sm text-gray-600">Running automated verification...</span>
              </div>
            </div>
          )}
          
          {verification.results && (
            <div className="mb-6 p-4 bg-white border border-gray-200 rounded-md">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Automated Verification Results</h4>
              
              {/* Required Checks */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">REQUIRED CHECKS:</p>
                {Object.entries(verification.results.required).map(([key, check]) => (
                  <div key={key} className="flex items-start mb-2">
                    {check.passed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    ) : (
                      <X className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${check.passed ? 'text-green-700' : 'text-red-700'}`}>
                      {check.message}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Advisory Checks */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">ADVISORY CHECKS:</p>
                {Object.entries(verification.results.advisory).map(([key, check]) => (
                  <div key={key} className="flex items-start mb-2">
                    {check.passed && !check.warning ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${check.passed && !check.warning ? 'text-green-700' : 'text-yellow-700'}`}>
                      {check.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Screenshots */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Screenshots <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Image className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label htmlFor="screenshots" className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none">
                    <span>Upload screenshots</span>
                    <input
                      id="screenshots"
                      name="screenshots"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      multiple
                      onChange={handleScreenshotChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG up to 10MB each</p>
              </div>
            </div>
            {screenshots.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                {screenshots.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Screenshot ${index + 1}`}
                      className="h-24 w-full object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Content File (Video or Photo) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Video or Photo <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Image className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label htmlFor="contentFile" className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none">
                    <span>Upload video or photo</span>
                    <input
                      id="contentFile"
                      name="contentFile"
                      type="file"
                      className="sr-only"
                      accept="image/*,video/*"
                      onChange={handleContentFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">Video or image up to 50MB</p>
              </div>
            </div>
            {contentFile && (
              <div className="mt-4">
                <p className="text-sm text-gray-700">
                  Selected: <span className="font-medium">{contentFile.name}</span>
                </p>
              </div>
            )}
          </div>
          
          {/* Additional Notes */}
          <div className="mb-6">
            <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              rows="3"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Any additional information about your content..."
            ></textarea>
          </div>
          
          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/influencer/campaigns/${campaignId}`)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || screenshotUploading || contentUploading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting || screenshotUploading || contentUploading ? (
                <>
                  <LoadingSpinner size="xs" color="white" />
                  <span className="ml-2">{isResubmission ? 'Resubmitting...' : 'Submitting...'}</span>
                </>
              ) : (
                <>
                  {isResubmission ? 'Resubmit Proof' : 'Submit Proof'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProofSubmission;
